// Renders the giveaway chooser on the product page and attaches the customer's
// choice to the line item.
//
// Two properties are written, both associated to the buy form by `form=` so this
// block can sit anywhere in the product layout:
//   properties[_giveaway_id]  hidden from the customer (leading underscore), read
//                             by oc-orders-api to assign the entries
//   properties[Giveaway]      the title, so cart and checkout show the choice
//
// Validation leans on the native constraint API: the radios are `required`, and
// the product form has no `novalidate`, so clicking Add to cart runs the
// browser's validation before Horizon's submit handler ever fires. The `invalid`
// event that dispatches is what reveals the inline error here.
//
// Express checkout (Buy it now, Shop Pay, Apple Pay…) is the exception: those
// buttons start checkout without submitting the form, so `required` never runs
// and orders reached checkout with no _giveaway_id. They are locked here until a
// giveaway is chosen — see syncBuyButtons.
//
// Degradation is deliberate: if the API is unreachable the grid renders empty,
// no `required` radio is ever inserted, and the purchase proceeds without a
// giveaway attached. Blocking checkout on a third-party lookup would trade lost
// entries for lost revenue. Reconcile unattached entries server-side.
(function () {
  const SELECTOR = "[data-oc-giveaway-picker]";

  function buildCard(giveaway, formId) {
    const label = document.createElement("label");
    label.className = "oc-giveaway-card";

    const input = document.createElement("input");
    input.type = "radio";
    input.className = "oc-giveaway-card__input";
    input.name = "properties[_giveaway_id]";
    input.value = giveaway._id;
    input.required = true;
    if (formId) input.setAttribute("form", formId);
    // Read back by the change handler so the visible title property stays in
    // step without a second lookup.
    input.dataset.giveawayTitle = giveaway.title || "";
    label.appendChild(input);

    const media = document.createElement("span");
    media.className = "oc-giveaway-card__media";
    if (giveaway.imgUrl) {
      const img = document.createElement("img");
      img.src = giveaway.imgUrl;
      img.alt = "";
      img.loading = "lazy";
      media.appendChild(img);
    }
    label.appendChild(media);

    const title = document.createElement("span");
    title.className = "oc-giveaway-card__title";
    // textContent, not innerHTML — titles come from the API and are merchandiser
    // input, so they are untrusted for markup purposes.
    title.textContent = giveaway.title || "";
    label.appendChild(title);

    return label;
  }

  // The express checkout container rendered inside this picker's product form
  // (blocks/accelerated-checkout.liquid). Horizon only toggles `hidden` on it for
  // availability and never re-renders it, so attributes set here persist.
  function expressCheckoutFor(picker) {
    const form = picker.dataset.formId && document.getElementById(picker.dataset.formId);
    return form ? form.querySelector(".accelerated-checkout-block") : null;
  }

  // Express checkout is locked while giveaways are still loading, and while there
  // are giveaways to pick from but none is picked. Once the lookup settles with
  // nothing to choose (API down, or no active giveaways) it unlocks, matching the
  // add-to-cart degradation described above.
  //
  // Add to cart is only faded, never disabled: its click is what runs the native
  // validation that shows the error. Flagged on the add-to-cart-component wrapper
  // because Horizon morphs the button itself on every variant change, which
  // would strip an attribute set on it.
  function syncBuyButtons(picker) {
    const radios = picker.querySelectorAll('input[name="properties[_giveaway_id]"]');
    const unchosen =
      radios.length > 0 &&
      !Array.prototype.some.call(radios, function (radio) {
        return radio.checked;
      });

    const form = picker.dataset.formId && document.getElementById(picker.dataset.formId);
    const addToCart = form && form.querySelector("add-to-cart-component");
    if (addToCart) addToCart.toggleAttribute("data-oc-giveaway-locked", unchosen);

    const block = expressCheckoutFor(picker);
    if (!block) return;

    const locked = radios.length ? unchosen : picker.dataset.loading === "true";
    block.toggleAttribute("data-oc-giveaway-locked", locked);
    // inert rather than pointer-events alone: it also takes the buttons out of
    // the tab order, and clicks fall through to the container, where the handler
    // in bind() turns them into the same error the Add to cart button shows.
    for (let i = 0; i < block.children.length; i++) {
      block.children[i].inert = locked;
    }
  }

  function render(picker) {
    const grid = picker.querySelector("[data-oc-giveaway-grid]");
    const error = picker.querySelector("[data-oc-giveaway-error]");
    const titleInput = picker.querySelector("[data-oc-giveaway-title]");
    if (!grid || !window.OCGiveaways) {
      syncBuyButtons(picker);
      return;
    }

    const formId = picker.dataset.formId || "";
    const giveaways = window.OCGiveaways.getAll();

    grid.textContent = "";

    // Nothing to choose: hide the whole block rather than leaving a heading over
    // an empty grid. No radios exist, so checkValidity() stays satisfied.
    if (!giveaways.length) {
      picker.hidden = true;
      if (titleInput) titleInput.value = "";
      syncBuyButtons(picker);
      return;
    }
    picker.hidden = false;

    const previous = picker.dataset.selectedId || "";
    let restored = false;

    giveaways.forEach(function (giveaway) {
      const card = buildCard(giveaway, formId);
      const input = card.querySelector("input");
      if (previous && String(giveaway._id) === previous) {
        input.checked = true;
        restored = true;
      }
      grid.appendChild(card);
    });

    // The previously chosen giveaway went inactive between renders — drop the
    // stale id rather than silently submitting one the API will reject.
    if (previous && !restored) {
      picker.dataset.selectedId = "";
      if (titleInput) titleInput.value = "";
    }

    if (restored && titleInput) {
      const current = window.OCGiveaways.getById(previous);
      titleInput.value = (current && current.title) || "";
    }

    if (error) error.hidden = true;
    syncBuyButtons(picker);
  }

  function bind(picker) {
    const error = picker.querySelector("[data-oc-giveaway-error]");
    const titleInput = picker.querySelector("[data-oc-giveaway-title]");

    picker.addEventListener("change", function (event) {
      const input = event.target;
      if (!input || input.name !== "properties[_giveaway_id]") return;
      picker.dataset.selectedId = input.value;
      if (titleInput) titleInput.value = input.dataset.giveawayTitle || "";
      if (error) error.hidden = true;
      syncBuyButtons(picker);
    });

    const expressCheckout = expressCheckoutFor(picker);
    if (expressCheckout) {
      expressCheckout.addEventListener("click", function () {
        if (!expressCheckout.hasAttribute("data-oc-giveaway-locked")) return;
        // Fires `invalid`, so the handler below shows the error and scrolls.
        const radio = picker.querySelector('input[name="properties[_giveaway_id]"]');
        if (radio) radio.reportValidity();
      });
    }

    // Horizon calls checkValidity() and returns without reporting, so this is
    // the only signal that the customer tried to add without choosing.
    picker.addEventListener(
      "invalid",
      function (event) {
        const input = event.target;
        if (!input || input.name !== "properties[_giveaway_id]") return;
        if (error) error.hidden = false;
        picker.scrollIntoView({ behavior: "smooth", block: "center" });
      },
      true // invalid does not bubble
    );

    document.addEventListener("oc:giveaways:updated", function () {
      render(picker);
    });
  }

  function init() {
    const pickers = document.querySelectorAll(SELECTOR);
    for (let i = 0; i < pickers.length; i++) {
      const picker = pickers[i];
      // Cold cache: the first fetch is still in flight (oc-giveaways.js starts
      // it on load), so hold express checkout locked until it settles.
      if (window.OCGiveaways && !window.OCGiveaways.getAll().length && window.OCGiveaways.isStale()) {
        picker.dataset.loading = "true";
        window.OCGiveaways.ready().then(function () {
          delete picker.dataset.loading;
          render(picker);
        });
      }
      bind(picker);
      render(picker);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
